import * as React from 'react';

import { cn } from '@shared/lib';

// TRAD-2099 W-14 — Checkbox.tsx와 짝(같은 배경·같은 신설 결정 2026-09-12, 팀장 확정).
// 라디오도 `src/shared/ui`에 없었다. 소비처 4곳(ReportForm.tsx의 신고 사유 선택 · ProposalFormPage.tsx
// 3곳 — 통화/가격조건/예외정책)이 각각 다른 방식으로 선택 상태를 관리한다:
//   - ReportForm.tsx: 네이티브 `name` 그룹핑(uncontrolled, onChange만) — `name` prop 그대로 통과.
//   - ProposalFormPage.tsx 3곳: `name` 없이 각 옵션의 `checked`를 상태로 개별 제어(React가
//     배타성을 담당) — 기존 설계를 그대로 보존한다(바꾸면 별도 회귀 위험이라 이 티켓 범위
//     밖). 그래서 이 컴포넌트는 `RadioGroup`으로 묶지 않고 `Radio` 낱개로 둔다 — 그룹핑
//     방식이 소비처마다 이미 다르므로 강제로 묶는 공용 그룹 컴포넌트를 얹으면 둘 중 하나의
//     설계를 깨야 한다.
//
// 기준 규격 = `Switch.tsx`/`Checkbox.tsx`와 동일(포커스링·disabled·크기 토큰), 네이티브
// `<input type="radio">` 래핑(Radix 의존 없음 — 이유는 Checkbox.tsx와 동일).

export interface RadioProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type' | 'size'> {
  /** 라디오 옆에 렌더할 라벨. ReactNode. 생략하면 label 없이 input만 렌더한다. */
  label?: React.ReactNode;
  /** label 텍스트(span) 자체에 추가로 줄 클래스. */
  labelClassName?: string;
  /** input+label을 감싸는 <label> 엘리먼트에 줄 클래스. */
  containerClassName?: string;
}

const Radio = React.forwardRef<HTMLInputElement, RadioProps>(
  ({ className, label, labelClassName, containerClassName, id, disabled, ...props }, ref) => {
    const generatedId = React.useId();
    const inputId = id ?? generatedId;

    const input = (
      <input
        type="radio"
        id={inputId}
        disabled={disabled}
        // 🔴 TRAD-3778 — Checkbox 와 같은 결함: 네이티브 모양(appearance:auto)이라 다크에서도 미선택이 흰 원이었다
        // (신고 사유 9개 실측 rgb(255,255,255)). appearance-none 으로 직접 그린다 — 미선택 = 투명 채움 + 테두리,
        // 선택 = theme-primary 테두리 + 가운데 점(`.radio-dot`, index.css).
        className={cn(
          'radio-dot h-4 w-4 shrink-0 cursor-pointer appearance-none rounded-full border border-muted-foreground bg-transparent transition-colors checked:border-theme-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:cursor-not-allowed disabled:opacity-50',
          className,
        )}
        ref={ref}
        {...props}
      />
    );

    if (label == null) return input;

    return (
      <label
        htmlFor={inputId}
        className={cn('inline-flex items-center gap-2 cursor-pointer', disabled && 'cursor-not-allowed', containerClassName)}
      >
        {input}
        <span className={labelClassName}>{label}</span>
      </label>
    );
  },
);
Radio.displayName = 'Radio';

export { Radio };
